import { useState } from "react";
import { cn } from "../lib/cn";
import { signIn } from "../lib/omauth";

/** A sign-in that started in another tab can't finish here (PKCE lives in that tab's storage). */
export function friendlySignInError(message: string): string {
  if (/orphan|no pending|no sign-in|not started|pending_authorization/i.test(message)) {
    return "That sign-in finished in a different tab, so it couldn't complete here. Try again.";
  }
  if (/could not be verified|state_mismatch/i.test(message)) {
    return "That sign-in was from an earlier attempt. Close any other sign-in windows and try again.";
  }
  return `${message} — please try again.`;
}

export type ConnectNote = { tone: "dim" | "error"; text: string } | null;

/** Sign in with OpenMarkets: the popup, and what to say when it's cancelled or fails. */
export function useConnect(initialError: string | null = null) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<ConnectNote>(
    initialError ? { tone: "error", text: friendlySignInError(initialError) } : null,
  );
  const connect = async () => {
    setBusy(true);
    setNote(null);
    const r = await signIn();
    setBusy(false);
    if (r.status === "denied") setNote({ tone: "dim", text: "No problem — connect whenever you're ready." });
    else if (r.status === "error") setNote({ tone: "error", text: friendlySignInError(r.message) });
  };
  return { busy, note, connect };
}

/** The sign-in CTA used in the top bar and the snapshot banner. */
export function ConnectButton({ label, size = "sm" }: { label: string; size?: "sm" | "md" }) {
  const { busy, note, connect } = useConnect();
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={connect}
        disabled={busy}
        className={cn(
          "brand-gradient inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-bold text-accent-ink shadow-[0_6px_24px_-10px_var(--color-accent)] transition-transform hover:-translate-y-px disabled:opacity-70",
          size === "md" ? "px-4 py-2 text-[13px]" : "px-3 py-1.5 text-[12px]",
        )}
      >
        {busy && (
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-accent-ink/60 border-t-transparent" />
        )}
        {busy ? "Waiting…" : label}
      </button>
      {note && (
        <span className={cn("max-w-64 text-right text-[11px]", note.tone === "error" ? "text-live" : "text-dim")}>
          {note.text}
        </span>
      )}
    </span>
  );
}
